import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import Modal from './Modal';
import DocumentList from './DocumentList';
import AccountFees from './AccountFees';
import { getUser, updateUser, deleteUsers, getUserHistory } from '../api/admin';
import ConfirmAccountDeletionModal, { referenceLabelFor } from './ConfirmAccountDeletionModal';
import { useAuth } from '../context/AuthContext';
import { useCountry } from '../context/CountryContext';

const ROLE_LABELS = {
  private_person: 'Private Person',
  vehicle_owner: 'Vehicle Owner',
  insurance_agency: 'Insurance Agency',
  admin: 'Admin (staff)',
  driver: 'Driver (KE)',
  bodaboda: 'Boda-Boda (KE)',
  hotel_host: 'Hotel / Vacation Rental Host (KE)',
  event_host: 'Wedding Hall / Event Host (KE)',
};

const HOST_ROLES = ['hotel_host', 'event_host'];
const FIELD_LABELS = {
  full_name: 'Name', email: 'E-mail', phone: 'Phone', address: 'Address', country: 'Country (as entered)',
  country_code: 'Country', date_of_birth: 'Date of birth', user_role: 'Account type',
};

const formFrom = (u) => ({
  full_name: u.full_name || '', email: u.email || '', phone: u.phone || '', address: u.address || '',
  country_code: u.country_code || '', date_of_birth: u.date_of_birth ? String(u.date_of_birth).slice(0, 10) : '',
  user_role: u.user_role, reason: '',
});

// All Users / Host Accounts › account details (Kayd, 18.10 + 26.10.2026):
// the account, its approval state, every submitted document — and editing
// and deleting the account (providers included), its fees & agreements and
// the change history.
export default function UserDetailModal({ userId, onClose, onChanged, onDeleted }) {
  const { permissions } = useAuth();
  const { availableOptions } = useCountry();
  const canManage = !!permissions?.manage_users;
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deletePending, setDeletePending] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [history, setHistory] = useState(null);

  const load = () => getUser(userId)
    .then((res) => setData(res.data))
    .catch((e) => setError(e.response?.data?.error || 'Could not load this account.'));
  const loadHistory = () => getUserHistory(userId).then((r) => setHistory(r.data.history || [])).catch(() => setHistory([]));

  useEffect(() => { load(); loadHistory(); }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const u = data?.user;
  const app = data?.provider_application;
  const isStaff = u?.user_role === 'admin';
  const countryCodes = availableOptions.filter((o) => o.code !== 'ALL').map((o) => o.code);
  if (u?.country_code && !countryCodes.includes(u.country_code)) countryCodes.push(u.country_code);

  const startEdit = () => { setForm(formFrom(u)); setFormError(''); setEditing(true); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true); setFormError('');
    try {
      const res = await updateUser(u.id, form);
      const n = Object.keys(res.data.changes || {}).length;
      toast.success(n ? 'Account saved' : 'Nothing changed');
      setEditing(false);
      await load(); loadHistory();
      if (n && onChanged) onChanged();
    } catch (err) {
      setFormError(err.response?.data?.error || 'The account could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async (confirm) => {
    setDeletePending(true); setDeleteError('');
    try {
      await deleteUsers([u.id], confirm);
      toast.success('Account deleted');
      setDeleting(false);
      if (onDeleted) onDeleted();
      onClose();
    } catch (err) {
      setDeleteError(err.response?.data?.error || 'The account could not be deleted.');
    } finally {
      setDeletePending(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <Modal title={u ? `${referenceLabelFor(u.user_role)} #${u.customer_number}` : 'Account details'} onClose={() => { if (!deleting) onClose(); }}>
      {error && <p style={{ color: '#b91c1c' }}>{error}</p>}
      {!data && !error && <p>Loading…</p>}
      {u && (
        <div style={s.wrap}>
          {canManage && !isStaff && !editing && (
            <div style={s.toolbar}>
              <button type="button" style={s.btn} onClick={startEdit}>Edit account</button>
              <button type="button" style={s.btnDanger} onClick={() => { setDeleteError(''); setDeleting(true); }}>Delete account</button>
            </div>
          )}

          {editing ? (
            <form onSubmit={save} style={s.form}>
              <div style={s.grid}>
                <Field label="Name *"><input style={s.input} value={form.full_name} onChange={set('full_name')} required /></Field>
                <Field label="E-mail *"><input style={s.input} type="email" value={form.email} onChange={set('email')} required /></Field>
                <Field label="Phone"><input style={s.input} value={form.phone} onChange={set('phone')} /></Field>
                <Field label="Date of birth"><input style={s.input} type="date" value={form.date_of_birth} onChange={set('date_of_birth')} /></Field>
                <Field label="Country">
                  <select style={s.input} value={form.country_code} onChange={set('country_code')}>
                    <option value="">—</option>
                    {countryCodes.map((c) => <option key={c} value={c}>{availableOptions.find((o) => o.code === c)?.label || c}</option>)}
                  </select>
                </Field>
                {HOST_ROLES.includes(u.user_role) && (
                  <Field label="Provider type">
                    <select style={s.input} value={form.user_role} onChange={set('user_role')}>
                      {HOST_ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                    </select>
                  </Field>
                )}
              </div>
              <Field label="Address"><textarea style={{ ...s.input, minHeight: 60 }} value={form.address} onChange={set('address')} /></Field>
              <Field label="Reason for the change (saved in the history)"><input style={s.input} value={form.reason} onChange={set('reason')} placeholder="e.g. provider asked by phone" /></Field>
              {formError && <p style={s.err} role="alert">{formError}</p>}
              <div style={s.toolbar}>
                <button type="button" style={s.btnGhost} onClick={() => setEditing(false)} disabled={saving}>Cancel</button>
                <button type="submit" style={s.btn} disabled={saving}>{saving ? 'Saving…' : 'Save changes'}</button>
              </div>
            </form>
          ) : (
            <div style={s.facts}>
              <Fact label="Name" value={u.full_name || '—'} />
              <Fact label="E-mail" value={u.email} />
              <Fact label="Phone" value={u.phone || '—'} />
              <Fact label="Role" value={ROLE_LABELS[u.user_role] || u.user_role} />
              <Fact label="Account" value={u.is_active ? 'Active' : 'Inactive'} />
              <Fact label="Approval" value={u.approval_status || '—'} />
              <Fact label="Market / country" value={[u.home_market, u.country_code].filter(Boolean).join(' / ') || '—'} />
              <Fact label="Date of birth" value={u.date_of_birth ? String(u.date_of_birth).slice(0, 10) : '—'} />
              <Fact label="Address" value={u.address || '—'} />
              <Fact label="Joined" value={u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'} />
            </div>
          )}

          {app && (
            <div style={s.appBox}>
              <div>
                <strong>Provider application:</strong> {app.status}
                {app.status === 'rejected' && app.admin_notes ? ` — ${app.admin_notes}` : ''}
              </div>
              <Link to={HOST_ROLES.includes(u.user_role) ? '/accommodation/provider-applications' : '/provider-applications'} style={s.link} onClick={onClose}>
                Review application →
              </Link>
            </div>
          )}

          {!isStaff && <AccountFees user={u} />}

          <h3 style={s.h3}>Submitted documents</h3>
          <DocumentList documents={data.documents} emptyText={isStaff ? 'Staff accounts have no documents.' : 'No documents submitted.'} />

          {history && history.length > 0 && (
            <details style={s.history}>
              <summary style={s.summary}>Change history ({history.length})</summary>
              {history.map((h) => (
                <div key={h.id} style={s.histRow}>
                  <div style={s.histHead}>
                    {new Date(h.created_at).toLocaleString()} · {h.admin?.full_name || h.admin?.email || 'Staff'} ·{' '}
                    {h.action === 'edit_user' ? 'edited' : h.action === 'block_user' ? 'blocked' : 'unblocked'}
                  </div>
                  {Object.entries(h.details?.changes || {}).map(([k, c]) => (
                    <div key={k} style={s.histLine}>{FIELD_LABELS[k] || k}: <s>{String(c.from ?? '—')}</s> → <strong>{String(c.to ?? '—')}</strong></div>
                  ))}
                  {(h.details?.reason) && <div style={s.histLine}>Reason: {h.details.reason}</div>}
                </div>
              ))}
            </details>
          )}
        </div>
      )}
      {deleting && u && (
        <ConfirmAccountDeletionModal
          accounts={[u]}
          pending={deletePending}
          serverError={deleteError}
          onCancel={() => setDeleting(false)}
          onConfirm={confirmDelete}
        />
      )}
    </Modal>
  );
}

function Field({ label, children }) {
  return (
    <label style={s.field}>
      <span style={s.factLabel}>{label}</span>
      {children}
    </label>
  );
}

function Fact({ label, value }) {
  return (
    <div style={s.fact}>
      <div style={s.factLabel}>{label}</div>
      <div style={s.factValue}>{value}</div>
    </div>
  );
}

const s = {
  wrap: { display: 'flex', flexDirection: 'column', gap: 14 },
  toolbar: { display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' },
  btn: { padding: '8px 14px', borderRadius: 8, border: 'none', background: '#0B6A4A', color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer' },
  btnDanger: { padding: '8px 14px', borderRadius: 8, border: '1px solid #fecaca', background: '#fff', color: '#b91c1c', fontWeight: 600, fontSize: 13, cursor: 'pointer' },
  btnGhost: { padding: '8px 14px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#374151', fontSize: 13, cursor: 'pointer' },
  form: { display: 'flex', flexDirection: 'column', gap: 10 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 10 },
  field: { display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 },
  input: { padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, width: '100%', boxSizing: 'border-box', fontFamily: 'inherit' },
  err: { color: '#b91c1c', fontSize: 13, margin: 0 },
  facts: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8 },
  fact: { background: '#f8fafc', borderRadius: 8, padding: '8px 10px', minWidth: 0 },
  factLabel: { fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' },
  factValue: { fontSize: 13.5, color: '#0f172a', marginTop: 2, wordBreak: 'break-word', textTransform: 'none' },
  appBox: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap',
    background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 12px', fontSize: 13.5,
  },
  link: { color: '#0B6A4A', fontWeight: 700, textDecoration: 'none', fontSize: 13 },
  h3: { fontSize: 14, fontWeight: 700, margin: 0 },
  history: { border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 10px' },
  summary: { cursor: 'pointer', fontWeight: 700, fontSize: 13 },
  histRow: { borderTop: '1px solid #f1f5f9', paddingTop: 6, marginTop: 6, fontSize: 12.5 },
  histHead: { color: '#64748b', marginBottom: 2 },
  histLine: { color: '#0f172a', wordBreak: 'break-word' },
};
